import { createRoot } from 'react-dom/client'
import { isIE, isFirefox } from 'react-device-detect'
import './index.css'
import type { AuthenticationResult } from '@azure/msal-browser'
import { InteractionType } from '@azure/msal-browser'
import { MsalProvider, MsalAuthenticationTemplate } from '@azure/msal-react'
import { QueryClientProvider } from '@tanstack/react-query'
import { getHopSkipConfig, loginRequest } from 'lib/auth/auth.config'
import { ErrorBoundary } from 'lib/contexts/errroBoundary.context'
import { StrictMode, Suspense } from 'react'
import { RealtimeProvider } from 'lib/providers/realtime.provider'
import { AppInsightsErrorBoundary } from '@microsoft/applicationinsights-react-js'
import { reactPlugin } from 'lib/auth/appInsights'
import ErrorFallback from 'components/errorFallback'
import loadAnalytics from 'lib/analytics/loadAnalytics'
import msalInstance from 'lib/auth/msal'
import queryClient from 'lib/auth/reactQuery'
import LoggedInUserProfileProvider from 'lib/providers/loggedInUserProfile.provider'
import UserProfileProvider from 'lib/providers/userProfile.provider'
import HSToast from 'components/toast'
import { BrowserRouter } from 'react-router-dom'
import App from 'App'
import PostHogProviderWrapper from 'lib/providers/posthog'
import PageLoader from 'components/pageLoader'
import AppReload from 'components/appReload'
import { APIProvider } from '@vis.gl/react-google-maps'

// Handle vite preload errors at the earliest point
// This catches errors that might occur before React components mount
globalThis.addEventListener('vite:preloadError', event => {
	console.log('Vite preload error detected at top level:', event)
	// Don't reload immediately - let AppReload component handle it with UI
	// This just ensures the event is captured early
})

const hopskipConfig = getHopSkipConfig()

async function initializeMsal(): Promise<void> {
	try {
		await msalInstance.initialize()
		const tokenResponse: AuthenticationResult | null =
			await msalInstance.handleRedirectPromise()
		console.log('Handle redirect response', tokenResponse)
	} catch (error) {
		console.error('Handle redirect error', error)
	}
}

function renderApp(): void {
	const container = document.querySelector('#root') as HTMLElement
	const root = createRoot(container)
	root.render(
		<StrictMode>
			<BrowserRouter
				future={{
					v7_relativeSplatPath: true
				}}
			>
				<Suspense fallback={<PageLoader />}>
					<PostHogProviderWrapper>
						<ErrorBoundary>
							<AppInsightsErrorBoundary
								onError={() => (
									<ErrorFallback
										error=''
										resetErrorBoundary={() => {
											globalThis.location.href = globalThis.location.origin
										}}
									/>
								)}
								appInsights={reactPlugin}
							>
								<MsalProvider instance={msalInstance}>
									<MsalAuthenticationTemplate
										interactionType={InteractionType.Redirect}
										authenticationRequest={loginRequest}
									>
										<QueryClientProvider client={queryClient}>
											<LoggedInUserProfileProvider>
												<UserProfileProvider>
													<RealtimeProvider>
														<APIProvider
															apiKey={hopskipConfig.googleMaps.key}
															libraries={['places', 'drawing', 'marker']}
														>
															<App />
														</APIProvider>
													</RealtimeProvider>
												</UserProfileProvider>
											</LoggedInUserProfileProvider>
										</QueryClientProvider>
									</MsalAuthenticationTemplate>
								</MsalProvider>
							</AppInsightsErrorBoundary>
							<HSToast />
						</ErrorBoundary>
					</PostHogProviderWrapper>
				</Suspense>
				<AppReload />
			</BrowserRouter>
		</StrictMode>
	)
}

function handleRedirection(): void {
	if (globalThis.location.search.includes('hsCtaTracking')) {
		globalThis.location.href = `${globalThis.location.origin}${globalThis.location.pathname}${globalThis.location.hash}`
	} else if (globalThis.location.pathname.includes('/id_token=')) {
		globalThis.location.href = `${globalThis.location.origin}${globalThis.location.pathname}`
	} else if (isIE || isFirefox) {
		globalThis.location.href = `${globalThis.location.origin}${globalThis.location.pathname}/browser-support.html`
	} else {
		renderApp()
	}
}

// eslint-disable-next-line unicorn/prefer-top-level-await
void (async function main(): Promise<void> {
	await initializeMsal()
	handleRedirection()
})()

loadAnalytics()
